import React, { useRef, useState } from 'react'
import { Banner } from './Banner'
import { Button } from './Button'
import { Modal } from './Modal'
import { uploadSeasonBackground } from '../services/api'
import {
    SEASON_BACKGROUND_REQUIRED_HEIGHT,
    SEASON_BACKGROUND_REQUIRED_WIDTH,
} from '../constants/seasonBackground'
import styles from './SeasonBackgroundModal.module.scss'

interface SeasonBackgroundImage {
    tc_image_id: string
    cdn_url: string
    thumbnail_url?: string
    pixel_width?: number
    pixel_height?: number
    aspect_ratio?: string
    mime_type?: string
    created_at?: string
    created_by?: string | null
}

interface SeasonForBackgroundModal {
    tc_season_id: string
    tc_name: string | null
    ext_name: string | null
    background_image: SeasonBackgroundImage | null
}

interface SeasonBackgroundModalProps {
    isOpen: boolean
    onClose: () => void
    season: SeasonForBackgroundModal | null
    onUploadSuccess: () => Promise<unknown> | unknown
}

const getSeasonLabel = (season: SeasonForBackgroundModal | null): string => {
    if (!season) return 'Unknown season'
    return season.tc_name || season.ext_name || 'Unknown season'
}

const formatUploadDate = (value?: string): string | null => {
    if (!value) return null
    try {
        return new Date(value).toLocaleDateString()
    } catch {
        return null
    }
}

const getUploadedMetaLabel = (
    createdAt?: string,
    createdBy?: string | null
): string | null => {
    const dateLabel = formatUploadDate(createdAt)
    if (dateLabel && createdBy) {
        return `Added ${dateLabel} by ${createdBy}`
    }
    if (dateLabel) {
        return `Added ${dateLabel}`
    }
    if (createdBy) {
        return `Uploaded by ${createdBy}`
    }
    return null
}

const getImageDetailsLabel = (
    pixelWidth?: number,
    pixelHeight?: number,
    mimeType?: string
): string | null => {
    const dimensions =
        pixelWidth && pixelHeight ? `${pixelWidth} x ${pixelHeight}` : null
    const parts = [dimensions, mimeType].filter(Boolean)
    return parts.length ? parts.join(' ') : null
}

const getUploadErrorMessage = (error: unknown, fallback: string): string => {
    if (error instanceof Error && error.message) {
        return error.message
    }
    return fallback
}

const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/webp'
const REQUIRED_WIDTH = SEASON_BACKGROUND_REQUIRED_WIDTH
const REQUIRED_HEIGHT = SEASON_BACKGROUND_REQUIRED_HEIGHT
const REQUIRED_RESOLUTION_LABEL = `${REQUIRED_WIDTH} x ${REQUIRED_HEIGHT}`

const getImageResolution = (file: File): Promise<{ width: number; height: number }> => (
    new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file)
        const img = new window.Image()
        img.onload = () => {
            resolve({ width: img.naturalWidth, height: img.naturalHeight })
            URL.revokeObjectURL(objectUrl)
        }
        img.onerror = () => {
            reject(new Error('Unable to read image dimensions'))
            URL.revokeObjectURL(objectUrl)
        }
        img.src = objectUrl
    })
)

export const SeasonBackgroundModal: React.FC<SeasonBackgroundModalProps> = ({
    isOpen,
    onClose,
    season,
    onUploadSuccess,
}) => {
    const [isUploading, setIsUploading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [isDragOver, setIsDragOver] = useState(false)
    const fileInputRef = useRef<HTMLInputElement | null>(null)

    const seasonLabel = getSeasonLabel(season)
    const hasBackground = Boolean(season?.background_image)
    const uploadedMetaLabel = getUploadedMetaLabel(
        season?.background_image?.created_at,
        season?.background_image?.created_by
    )
    const imageDetailsLabel = getImageDetailsLabel(
        season?.background_image?.pixel_width,
        season?.background_image?.pixel_height,
        season?.background_image?.mime_type
    )

    const closeAndReset = () => {
        setError(null)
        setIsDragOver(false)
        onClose()
    }

    const selectFile = () => {
        fileInputRef.current?.click()
    }

    const handleUploadFile = async (file: File) => {
        if (!season) return

        try {
            const { width, height } = await getImageResolution(file)
            if (width !== REQUIRED_WIDTH || height !== REQUIRED_HEIGHT) {
                setError(
                    `Image must be exactly ${REQUIRED_WIDTH}x${REQUIRED_HEIGHT}. Uploaded image is ${width}x${height}.`
                )
                if (fileInputRef.current) fileInputRef.current.value = ''
                return
            }
        } catch {
            setError('Unable to read image dimensions. Please try another file.')
            if (fileInputRef.current) fileInputRef.current.value = ''
            return
        }

        setIsUploading(true)
        setError(null)
        try {
            await uploadSeasonBackground(season.tc_season_id, file)
            await onUploadSuccess()
            closeAndReset()
        } catch (err) {
            setError(getUploadErrorMessage(err, 'Upload failed. Please try again.'))
        } finally {
            setIsUploading(false)
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        }
    }

    const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return
        await handleUploadFile(file)
    }

    const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        setIsDragOver(false)
        if (isUploading) return

        const file = event.dataTransfer.files?.[0]
        if (!file) return
        await handleUploadFile(file)
    }

    if (!season) {
        return null
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={closeAndReset}
            title="Season Background"
            size="medium"
            disableBodyScroll
            headerActions={
                <Button variant="secondary" onClick={closeAndReset} disabled={isUploading}>
                    Close
                </Button>
            }
            footer={
                hasBackground ? (
                    <>
                        <Button variant="secondary" onClick={selectFile} disabled={isUploading}>
                            {isUploading ? 'Uploading...' : 'Replace Image'}
                        </Button>
                    </>
                ) : (
                    <Button variant="secondary" onClick={closeAndReset} disabled={isUploading}>
                        Cancel
                    </Button>
                )
            }
        >
            <div className={styles.subtitle}>{seasonLabel}</div>

            {error && (
                <div className={styles.banner}>
                    <Banner variant="error" message={error} onDismiss={() => setError(null)} />
                </div>
            )}

            {hasBackground && season.background_image ? (
                <div className={styles.previewContainer}>
                    <img
                        src={season.background_image.cdn_url}
                        alt={`${seasonLabel} background`}
                        className={styles.previewImage}
                    />
                    <div className={styles.metadata}>
                        {imageDetailsLabel && (
                            <div className={styles.metadataLine}>{imageDetailsLabel}</div>
                        )}
                        {uploadedMetaLabel && (
                            <div className={styles.metadataLine}>{uploadedMetaLabel}</div>
                        )}
                    </div>
                    {isUploading && <p className={styles.uploadingState}>Uploading...</p>}
                </div>
            ) : (
                <div
                    className={`${styles.dropZone} ${isDragOver ? styles.dragOver : ''}`}
                    onClick={() => !isUploading && selectFile()}
                    onDrop={handleDrop}
                    onDragOver={(event) => {
                        event.preventDefault()
                        if (!isUploading) setIsDragOver(true)
                    }}
                    onDragLeave={() => setIsDragOver(false)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                        if (!isUploading && (event.key === 'Enter' || event.key === ' ')) {
                            event.preventDefault()
                            selectFile()
                        }
                    }}
                    aria-label={`Upload background image for ${seasonLabel}`}
                >
                    <div className={styles.dropZoneIcon}>📷</div>
                    <p className={styles.dropZoneTitle}>{isUploading ? 'Uploading...' : 'Drop image here or click to browse'}</p>
                    <p className={styles.dropZoneHint}>Required size: {REQUIRED_RESOLUTION_LABEL}. Max size: 50MB.</p>
                    <p className={styles.dropZoneHint}>Formats: JPEG, PNG, WebP</p>
                </div>
            )}

            <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_IMAGE_TYPES}
                onChange={handleFileInputChange}
                className={styles.hiddenInput}
                disabled={isUploading}
            />
        </Modal>
    )
}

